import { yupResolver } from '@hookform/resolvers/yup';
import EditIcon from '@mui/icons-material/Edit';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import LoadingButton from '@mui/lab/LoadingButton';
import { Box, Button, Stack, Tab, Tabs } from '@mui/material';
import routes from 'config/routesConfig';
import Loading from 'core/components/Loading';
import useCurrentRoute from 'core/hooks/useCurrentRoute';
import { useModuleAccess } from 'hooks/useModuleAccess';
import { intentionallyFloatingPromiseReturn, isNumeric } from 'core/utils/commonUtils';
import {
  getDefaultOrder,
  getGeolocationString,
  getOrderStateName,
  parseGeolocationString,
} from 'core/utils/orderUtils';
import dayjs from 'dayjs';
import useOrder, { SWR_ORDER_ENDPOINT, mutateOrder } from 'hooks/swr/useOrder';
import useOrderMangoCalls from 'hooks/swr/useOrderMangoCalls';
import CardedPage from 'layout/pages/CardedPage';
import { isArray, isEmpty, isNil, toNumber } from 'lodash';
import { useConfirm } from 'material-ui-confirm';
import { observer } from 'mobx-react-lite';
import { ClientDocumentTypeEnum } from 'models/enums/clientDocumentTypeEnum';
import { OrderStateEnum } from 'models/enums/orderStateEnum';
import { useContext, useEffect, useRef, useState } from 'react';
import { FormContainer, useForm } from 'react-hook-form-mui';
import { Link as RouterLink, generatePath, useParams } from 'react-router-dom';
import * as clientService from 'services/clientService';
import * as orderService from 'services/orderService';
import StoreContext from 'stores/context/StoreContext';
import { mutate } from 'swr';
import OrderActionsEditor from './Actions/OrderActionsEdit';
import OrderDocuments from './Documents/OrderDocuments';
import OrderFiles from './Files/OrderFiles';
import ImageGallery from './ImageGallery/ImageGallery';
import OrderEditPageHeader from './OrderEditPageHeader';
import OrderForm from './OrderForm';
import OrderView from './OrderView';
import OrderPaymentsEdit from './Payments/OrderPaymentsEdit';
import OrderPayoutsEdit from './Payouts/OrderPayoutsEdit';
import OrderProjectsEdit from './Projects/OrderProjectsEdit';
import OrderDiscountsEdit from './Discounts/OrderDiscountsEdit';
import OrderMangoCalls from './MangoCalls/OrderMangoCalls';
import MangoCallsPlayer from 'core/components/MangoCallsPlayer';
import type { MangoCallsPlayerHandle } from 'core/components/MangoCallsPlayer';
import orderFormSchema from './orderFormSchema';
import { useOrderPageStore } from './store/useOrderPageStore';
import type OrderFormData from './orderFormData';
import type { Order } from 'models/order/order';
import type { MangoCallInfo } from 'models/mangoCall';
import type { FC, PropsWithChildren } from 'react';
import type { CreateOrUpdateOrderRequest } from 'services/dtoModels/order/createOrUpdateOrderRequest';

interface TabPanelProps {
  tabName: string;
  value: string;
}

const TabPanel: FC<PropsWithChildren<TabPanelProps>> = (props) => {
  const { children, value, tabName, ...other } = props;

  return (
    <div
      role='tabpanel'
      hidden={value !== tabName}
      id={`order-edit-tabpanel-${tabName}`}
      aria-labelledby={`order-edit-tab-${tabName}`}
      {...other}>
      {value === tabName && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
};

const tabA11yProps = (tabName: string): Record<string, unknown> => ({
  id: `order-edit-tab-${tabName}`,
  'aria-controls': `order-edit-tabpanel-${tabName}`,
});

const orderToFormData = (order: Order): OrderFormData => ({
  ...order,
  geolocation:
    isNil(order) || isNil(order?.geolocation)
      ? ''
      : getGeolocationString(order.geolocation),
  client:
    isNil(order.client) || isNil(order.client)
      ? null
      : {
          ...order.client,
          docDateOfIssue: isNil(order.client.docDateOfIssue)
            ? null
            : dayjs(order.client.docDateOfIssue),
        },
});

const OrderEditPage: FC = observer(() => {
  const currentRoute = useCurrentRoute(routes.routes);
  const { orderId } = useParams();
  const [mode, setMode] = useState<'edit' | 'view'>('view');
  const changeStateConfirm = useConfirm();
  const { isModuleEnabled } = useModuleAccess();

  const orderPageStore = useOrderPageStore();
  const { notifyStore, userStore } = useContext(StoreContext);

  const formContext = useForm<OrderFormData>({
    mode: 'onTouched',
    defaultValues: isNil(orderPageStore.order)
      ? undefined
      : orderToFormData(orderPageStore.order),
    resolver: yupResolver(orderFormSchema),
  });

  const { isSubmitting } = formContext.formState;

  useEffect(() => {
    formContext.reset(orderToFormData(orderPageStore.order ?? getDefaultOrder()));
  }, [orderPageStore.order, formContext]);

  const { data, isLoading } = useOrder(toNumber(orderId), isNumeric(orderId ?? ''));
  const { data: orderMangoCalls } = useOrderMangoCalls(
    toNumber(orderId),
    isNumeric(orderId ?? '')
  );
  const [selectedMangoCall, setSelectedMangoCall] = useState<MangoCallInfo | null>(null);
  const [isMangoCallPlaying, setIsMangoCallPlaying] = useState(false);
  const mangoPlayerRef = useRef<MangoCallsPlayerHandle>(null);

  useEffect(() => {
    const order = data ? structuredClone(data) : getDefaultOrder();

    orderPageStore.updateOrder(order);
  }, [data, orderPageStore]);

  useEffect(() => {
    if (mode === 'edit')
      formContext.reset(orderToFormData(orderPageStore.order ?? getDefaultOrder()));
  }, [formContext, mode, orderPageStore.order]);

  const tabs: Array<[string, string]> = [
    ['Общее', 'common'],
    ['Проекты', 'projects'],
    ['Этапы', 'actions'],
    ...(isModuleEnabled('images') ? [['Фото', 'photos'] as [string, string]] : []),
    ...(isModuleEnabled('files') ? [['Файлы', 'files'] as [string, string]] : []),
    ['Платежи', 'payments'],
    ...(userStore.ability?.can('read', 'Payouts') && isModuleEnabled('payouts')
      ? [['Выплаты', 'payouts'] as [string, string]]
      : []),
    ...(userStore.ability?.can('read', 'OrderDiscounts') && isModuleEnabled('discounts')
      ? [['Скидки', 'discounts'] as [string, string]]
      : []),
    ['Документы', 'documents'],
    ...((orderMangoCalls?.length ?? 0) > 0
      ? [['Звонки', 'calls'] as [string, string]]
      : []),
  ];
  const currentTab = currentRoute?.params.tab ?? 'common';

  const pageTitle =
    !isNil(orderPageStore.order?.number) &&
    !isEmpty(orderPageStore.order?.number) &&
    !isNil(orderPageStore.order)
      ? `${getOrderStateName(orderPageStore.order.state)} ${orderPageStore.order.number}`
      : 'Новая заявка';

  const handleOnOrderSave = async (): Promise<void> => {
    try {
      // Сделано так специально, т к если брать данные из аргументов функции, то там почему-то Dayjs преобразуется в Date
      const formData = formContext.getValues();
      const client = formData.client;

      if (isNil(client)) {
        throw new Error('Клиент не задан');
      }

      const existClient = await clientService.getClientAsync(
        orderPageStore.order?.client.id ?? 0
      );

      if (isNil(existClient)) {
        throw new Error('Клиент не найден');
      }

      await clientService.updateClientAsync(existClient.id, {
        ...client,
        phone: client?.phone?.replace(/\D/, '') ?? '',
        docType: existClient.docType ?? ClientDocumentTypeEnum.Passport,
        docDateOfIssue: client.docDateOfIssue?.toDate(),
      });

      const tmpOrder: CreateOrUpdateOrderRequest = {
        address: formData.address,
        remark: formData.remark,
        warningComment: formData.warningComment,
        clientId: existClient.id,
        state: orderPageStore.order?.state ?? OrderStateEnum.Request,
        geolocation: !isEmpty(formData.geolocation)
          ? parseGeolocationString(formData.geolocation)
          : undefined,
      };

      const save = async (): Promise<Order> =>
        isNumeric(orderId ?? '')
          ? await orderService.updateOrderAsync(toNumber(orderId), tmpOrder)
          : await orderService.createOrderAsync(tmpOrder);

      await mutate((key) => isArray(key) && key[0] === SWR_ORDER_ENDPOINT, save(), {
        populateCache: true,
        throwOnError: true,
        rollbackOnError: true,
        revalidate: false,
      });

      setMode('view');
    } catch (err) {
      console.log(err);
    }
  };

  const toEditModeHandler = (): void => {
    setMode('edit');
  };

  const toViewMode = (): void => {
    setMode('view');
  };

  const setOrderState = async (state: OrderStateEnum): Promise<void> => {
    try {
      await changeStateConfirm({
        description: (
          <>
            <div>
              Статус заказа будет измен на &quot;{getOrderStateName(state)}&quot;.
            </div>
            <div>Продолжить?</div>
          </>
        ),
      });

      await orderService.setOrderState({ orderIds: [+(orderId ?? 0)], state });

      notifyStore.showNotify('Статус заказа изменен', 'success');

      await mutateOrder();
    } catch (e) {
      console.log(e);
    }
  };

  if (isLoading || isNil(orderPageStore.order)) {
    return <Loading />;
  }

  return (
    <>
      <CardedPage
        disablePadding
        title={pageTitle}
        header={
          <OrderEditPageHeader
            orderNumber={orderPageStore.order?.number}
            orderState={orderPageStore.order?.state}
            orderDateCreate={orderPageStore.order?.dateCreate}
            onArchiveOrder={() => {
              intentionallyFloatingPromiseReturn(setOrderState)(OrderStateEnum.Archive);
            }}
            onUnarchiveOrder={() => {
              intentionallyFloatingPromiseReturn(setOrderState)(OrderStateEnum.Request);
            }}
            onWorkOrder={() => {
              intentionallyFloatingPromiseReturn(setOrderState)(OrderStateEnum.Order);
            }}
          />
        }>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={currentTab}
            aria-label='basic tabs'
            variant='scrollable'
            scrollButtons='auto'>
            {tabs.map(([label, tabName]) => (
              <Tab
                key={tabName}
                label={label}
                {...tabA11yProps(tabName)}
                value={tabName}
                to={generatePath(currentRoute?.route.path ?? '', {
                  ...currentRoute?.params,
                  tab: tabName,
                })}
                component={RouterLink}
              />
            ))}
          </Tabs>
        </Box>

        <TabPanel value={currentTab} tabName={'common'}>
          {mode === 'view' ? (
            <OrderView
              actions={
                <Button
                  startIcon={<EditIcon />}
                  variant='outlined'
                  onClick={toEditModeHandler}>
                  Редактировать
                </Button>
              }
            />
          ) : (
            <FormContainer formContext={formContext} onSuccess={handleOnOrderSave}>
              <OrderForm />

              <Stack direction={'row'} justifyContent={'end'} gap={2}>
                <LoadingButton
                  startIcon={<SaveOutlinedIcon />}
                  variant='contained'
                  type='submit'
                  loadingPosition='start'
                  loading={isSubmitting}>
                  <span>Сохранить</span>
                </LoadingButton>
                <Button variant='outlined' type='button' onClick={toViewMode}>
                  Отмена
                </Button>
              </Stack>
            </FormContainer>
          )}
        </TabPanel>

        <TabPanel value={currentTab} tabName={'projects'}>
          <OrderProjectsEdit orderId={+(orderId ?? 0)} />
        </TabPanel>

        <TabPanel value={currentTab} tabName={'actions'}>
          <OrderActionsEditor />
        </TabPanel>

        <TabPanel value={currentTab} tabName={'payments'}>
          <OrderPaymentsEdit orderId={+(orderId ?? 0)} />
        </TabPanel>

        <TabPanel value={currentTab} tabName={'payouts'}>
          <OrderPayoutsEdit orderId={+(orderId ?? 0)} />
        </TabPanel>

        <TabPanel value={currentTab} tabName={'discounts'}>
          <OrderDiscountsEdit orderId={+(orderId ?? 0)} />
        </TabPanel>

        <TabPanel value={currentTab} tabName={'photos'}>
          <ImageGallery orderId={+(orderId ?? 0)} />
        </TabPanel>

        <TabPanel value={currentTab} tabName={'documents'}>
          <OrderDocuments orderId={+(orderId ?? 0)} />
        </TabPanel>

        <TabPanel value={currentTab} tabName={'files'}>
          <OrderFiles orderId={+(orderId ?? 0)} />
        </TabPanel>

        <TabPanel value={currentTab} tabName={'calls'}>
          <OrderMangoCalls
            orderId={+(orderId ?? 0)}
            selectedCall={selectedMangoCall}
            isPlaying={isMangoCallPlaying}
            onSelectCall={setSelectedMangoCall}
            onTogglePlay={() => mangoPlayerRef.current?.togglePlay()}
          />
        </TabPanel>
      </CardedPage>

      {selectedMangoCall != null && (
        <>
          <Box sx={{ height: '130px', flexShrink: 0 }} />
          <MangoCallsPlayer
            ref={mangoPlayerRef}
            call={selectedMangoCall}
            showSkipControls={(orderMangoCalls?.length ?? 0) > 1}
            onPrev={() => {
              const items = orderMangoCalls ?? [];
              const idx = items.findIndex((c) => c.id === selectedMangoCall.id);
              setSelectedMangoCall(items[idx - 1] ?? null);
            }}
            onNext={() => {
              const items = orderMangoCalls ?? [];
              const idx = items.findIndex((c) => c.id === selectedMangoCall.id);
              setSelectedMangoCall(items[idx + 1] ?? null);
            }}
            onClose={() => {
              setSelectedMangoCall(null);
            }}
            onPlayStateChange={setIsMangoCallPlaying}
          />
        </>
      )}
    </>
  );
});

export default OrderEditPage;
